import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  audienceVoteApi,
  type UpdateAudienceVoteEventBody,
  type UpsertAudienceVoteContestantBody,
} from "./client";

const keys = {
  events: ["audience-vote-events"] as const,
  event: (id: string) => ["audience-vote-events", id] as const,
};

export function useAudienceVoteEventsQuery() {
  return useQuery({
    queryKey: keys.events,
    queryFn: () => audienceVoteApi.listEvents(),
  });
}

export function useAudienceVoteEventQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: keys.event(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("eventId required");
      return audienceVoteApi.getEvent(eventId);
    },
    enabled: !!eventId,
  });
}

export function useUpdateAudienceVoteEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      body,
    }: {
      eventId: string;
      body: UpdateAudienceVoteEventBody;
    }) => audienceVoteApi.updateEvent(eventId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function usePatchAudienceVoteEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      body,
    }: {
      eventId: string;
      body: Parameters<typeof audienceVoteApi.patchEvent>[1];
    }) => audienceVoteApi.patchEvent(eventId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useCreateAudienceVoteContestantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      body,
    }: {
      eventId: string;
      body: Parameters<typeof audienceVoteApi.createContestant>[1];
    }) => audienceVoteApi.createContestant(eventId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}

export function useUpdateAudienceVoteContestantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      contestantId,
      body,
    }: {
      eventId: string;
      contestantId: string;
      body: UpsertAudienceVoteContestantBody;
    }) => audienceVoteApi.updateContestant(eventId, contestantId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}

export function useDeleteAudienceVoteContestantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      contestantId,
    }: {
      eventId: string;
      contestantId: string;
    }) => audienceVoteApi.deleteContestant(eventId, contestantId),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}
